/**
 * Eine Apartmentzeile der Belegung als reines Modell: welche Balken in welcher Bahn liegen, welche
 * Nächte doppelt belegt sind und was abgeblendet wird. Grundlage sind nur die Spans und Zellen des
 * Servers; überschneiden sich zwei eigene Buchungen, liegen sie in zwei Bahnen übereinander, statt
 * dass eine die andere verdeckt.
 */
import type { CalendarPropertyRow } from '@/shared/api';
import { calendarCellState } from './calendarFamily';
import {
  bookedDayCount,
  buildBookingSpans,
  classifyChannel,
  matchesStayFilter,
  shouldRenderBookingPill,
  type BookingSpan,
  type ChannelSlug,
  type StayFilter,
} from './calendarHelpers';

export type Lane = 0 | 1 | 2;
const MAX_LANES = 3;

export interface CalendarBarModel {
  span: BookingSpan;
  /** 0 oben, dann darunter; nur relevant, wenn die Zeile Bahnen hat. */
  lane: Lane;
  dimmed: boolean;
}

export interface CalendarRowModel {
  bars: CalendarBarModel[];
  /** Zwei eigene Buchungen überschneiden sich: die Zeile teilt sich in zwei Bahnen. */
  laned: boolean;
  lanes: 1 | 2 | 3;
  /** Zellen, in denen zwei Buchungen oder Buchung und Verbund dieselbe Nacht belegen. */
  doubleNights: Set<number>;
  occupancyPct: number;
}

interface Filters {
  enabledChannels: Set<ChannelSlug>;
  stayFilter: StayFilter;
  /** Der Aufenthaltsfilter misst an heute, nicht am gezeigten Zeitraum. */
  today: string;
}

const endOf = (span: BookingSpan) => span.startIndex + span.nights;

function overlapNights(a: BookingSpan, b: BookingSpan): number[] {
  const nights: number[] = [];
  for (
    let index = Math.max(a.startIndex, b.startIndex);
    index < Math.min(endOf(a), endOf(b));
    index += 1
  ) {
    nights.push(index);
  }
  return nights;
}

export function buildRowModel(row: CalendarPropertyRow, filters: Filters): CalendarRowModel {
  const spans = buildBookingSpans(row.spans)
    .filter(shouldRenderBookingPill)
    .sort((a, b) => a.startIndex - b.startIndex);
  const doubleNights = new Set<number>();
  row.cells.forEach((cell, index) => {
    if (calendarCellState(cell) === 'doubleBooked') doubleNights.add(index);
  });
  let laned = false;
  spans.forEach((a, i) =>
    spans.slice(i + 1).forEach((b) => {
      const shared = overlapNights(a, b);
      if (shared.length > 0) laned = true;
      shared.forEach((night) => doubleNights.add(night));
    }),
  );
  // Greedy: jede Buchung nimmt die oberste Bahn, die bis zu ihrem Beginn frei ist (höchstens drei).
  const laneEnds: number[] = [];
  const bars = spans.map((span): CalendarBarModel => {
    let lane = 0;
    if (laned) {
      const free = laneEnds.findIndex((end) => span.startIndex >= end);
      lane = free === -1 ? Math.min(laneEnds.length, MAX_LANES - 1) : free;
      laneEnds[lane] = Math.max(laneEnds[lane] ?? Number.NEGATIVE_INFINITY, endOf(span));
    }
    return {
      span,
      lane: lane as Lane,
      dimmed:
        !filters.enabledChannels.has(classifyChannel(span.channel).slug) ||
        !matchesStayFilter(span, filters.today, filters.stayFilter),
    };
  });
  const days = row.cells.length;
  return {
    bars,
    laned,
    lanes: laned ? (Math.min(laneEnds.length, MAX_LANES) as 2 | 3) : 1,
    doubleNights,
    occupancyPct: days === 0 ? 0 : Math.round((bookedDayCount(row) / days) * 100),
  };
}

/** Ob die Zeile das Zeichen „Doppelt belegt“ trägt – für Chip und Legende. */
export const rowIsDoubleBooked = (model: CalendarRowModel) => model.doubleNights.size > 0;

/** „2 Erw. · 1 Kind“ – kurz für den Balken; ohne verlässliche Aufteilung bleibt der Hinweis. */
export function occupancyShort(
  span: Pick<
    BookingSpan,
    'guestCount' | 'adults' | 'children' | 'infants' | 'occupancyDataQualityCode'
  >,
): string {
  if (span.occupancyDataQualityCode !== 'Verified') {
    return `${span.guestCount} Gäste · Aufteilung nicht verifiziert`;
  }
  const parts = [`${span.adults} Erw.`];
  if (span.children) parts.push(`${span.children} ${span.children === 1 ? 'Kind' : 'Kinder'}`);
  if (span.infants) {
    parts.push(`${span.infants} ${span.infants === 1 ? 'Kleinkind' : 'Kleinkinder'}`);
  }
  return parts.join(' · ');
}
